/**
 * InvalidTranscriptOverlay Component
 * 
 * Full-screen overlay displayed when the AI detects that audio quality is too poor
 * and the transcript is unusable. Shows explanation and restart button.
 */

import { useTranslation } from 'react-i18next';

interface InvalidTranscriptOverlayProps {
  reason?: string;
  onRestart: () => void;
}

export default function InvalidTranscriptOverlay({ reason: _reason, onRestart }: InvalidTranscriptOverlayProps) {
  const { t } = useTranslation();
  
  return (
    <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-md flex items-center justify-center z-[70] pointer-events-auto p-4">
      <div className="bg-gradient-to-br from-red-900/90 to-red-800/90 border-4 border-red-500 rounded-2xl md:rounded-3xl px-4 py-6 md:px-12 md:py-10 shadow-2xl max-w-2xl w-full">
        <div className="text-center">
          {/* Icon */}
          <div className="text-5xl md:text-7xl mb-4 md:mb-6">🎤❌</div>
          
          {/* Title */}
          <div className="text-2xl md:text-4xl font-bold text-white mb-3 md:mb-4">
            {t('scenario.session.audioQualityPoor')}
          </div>
          
          {/* Explanation */}
          <div className="text-base md:text-xl text-red-100 mb-4 md:mb-6 leading-relaxed">
            {t('scenario.session.audioQualityExplanation')}
          </div>
          
          {/* Tips */}
          <div className="bg-black/30 rounded-xl p-4 md:p-6 mb-4 md:mb-6 text-left">
            <div className="text-base md:text-lg font-semibold text-white mb-2 md:mb-3">
              💡 {t('scenario.session.audioTips')}
            </div>
            <ul className="text-red-100 space-y-1 md:space-y-2 text-sm md:text-base">
              <li>• {t('scenario.session.audioTip1')}</li>
              <li>• {t('scenario.session.audioTip2')}</li>
              <li>• {t('scenario.session.audioTip3')}</li>
            </ul>
          </div>
          
          {/* Note: reason field from backend is not displayed as it's in English
              and doesn't respect user's language selection. The translated messages
              above are sufficient for user communication. */}
          
          {/* Restart button */}
          <button
            onClick={onRestart}
            className="mt-4 px-10 py-4 bg-gradient-to-r from-white to-gray-100 hover:from-gray-100 hover:to-white text-red-900 font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-200 transform hover:scale-105 text-xl"
          >
            {t('scenario.session.restartScenario')}
          </button>
        </div>
      </div>
    </div>
  );
}


